// Generic SVG tyre map: renders any vehicle layout (axles top-to-bottom).
// Props:
//   layoutCode: key into VEHICLE_LAYOUTS
//   tyres: list of fitted tyres (must have current_position matching layout codes)
//   selectedPosition: optional, highlights a position
//   onPositionClick: optional, called with code; turns tile into picker
import { getLayout } from "@/lib/vehicleLayouts";

const COLORS = { ok: "#10B981", watch: "#F59E0B", bad: "#E11D48", empty: "#FFFFFF", emptyStroke: "#CBD5E1" };

// Color-code a fitted tyre by status + retread count.
// - fitted fresh (0 retreads): green
// - fitted retreaded (1+ retreads): amber (watch)
// - retreaded in stock / scrapped / other: red
function colorForTyre(t) {
    if (!t) return COLORS.empty;
    if (t.status === "fitted") return (t.retread_count || 0) >= 1 ? COLORS.watch : COLORS.ok;
    return COLORS.bad;
}

export default function TyreMap({ layoutCode, tyres = [], selectedPosition, onPositionClick }) {
    const layout = getLayout(layoutCode);
    if (!layout) {
        return <div className="text-sm text-fleet-inkSoft border border-dashed border-fleet-border rounded-md p-4 text-center">No layout — set vehicle type to view tyre positions.</div>;
    }

    const byPos = Object.fromEntries(tyres.map((t) => [t.current_position, t]));

    // SVG layout dimensions
    const TYRE_W = 22, TYRE_H = 32, GAP = 4;
    const AXLE_GAP = 56;
    const PAD_X = 50, PAD_Y = 30;

    // Determine widest axle (4 tyres + 2 inner gaps)
    const maxTyresPerSide = 2; // dual = 2 per side max
    const sideW = maxTyresPerSide * (TYRE_W + GAP) + 12;
    const chassisW = sideW * 2 + 60; // gap in middle
    const totalW = chassisW + PAD_X * 2;
    const spareBayH = layout.spare ? 46 : 0;
    const totalH = layout.axles.length * AXLE_GAP + PAD_Y * 2 + 40 + spareBayH;

    const spareTyre = layout.spare ? byPos[layout.spare.code] : null;
    const spareY = PAD_Y + layout.axles.length * AXLE_GAP + 60;
    const spareFill = spareTyre ? colorForTyre(spareTyre) : COLORS.empty;
    const spareStroke = selectedPosition === layout.spare?.code
        ? "#0F172A" : spareTyre ? "#0F172A" : COLORS.emptyStroke;
    const spareStrokeW = selectedPosition === layout.spare?.code ? 2.5 : 1.5;
    const spareClickable = !!onPositionClick;

    return (
        <div className="flex gap-6">
            <svg viewBox={`0 0 ${totalW} ${totalH}`} className="shrink-0" style={{ width: 320, height: totalH * (320 / totalW) }} data-testid="tyre-map-svg">
                {/* Chassis */}
                <rect x={PAD_X} y={PAD_Y} width={chassisW} height={totalH - PAD_Y * 2} rx={10} fill="#F1F5F9" stroke="#CBD5E1" strokeWidth={1.2} />
                {/* Cab indicator */}
                <rect x={PAD_X + chassisW * 0.2} y={PAD_Y + 6} width={chassisW * 0.6} height={26} rx={4} fill="#FFFFFF" stroke="#CBD5E1" />
                <text x={PAD_X + chassisW / 2} y={PAD_Y + 22} textAnchor="middle" fontSize="10" fontFamily="IBM Plex Mono" fill="#64748B">CAB</text>

                {layout.axles.map((axle, idx) => {
                    const y = PAD_Y + 50 + idx * AXLE_GAP;
                    const cx = PAD_X + chassisW / 2;
                    // Axle bar
                    return (
                        <g key={axle.axle}>
                            <line x1={PAD_X + 10} y1={y} x2={PAD_X + chassisW - 10} y2={y} stroke="#CBD5E1" strokeWidth={1} />
                            <text x={PAD_X + 8} y={y + 4} textAnchor="end" fontSize="9" fontFamily="IBM Plex Mono" fill="#94A3B8">{axle.axle}</text>
                            {axle.tyres.map((tdef) => {
                                const tyre = byPos[tdef.code];
                                const fill = tyre ? colorForTyre(tyre) : COLORS.empty;
                                const stroke = selectedPosition === tdef.code
                                    ? "#0F172A"
                                    : tyre ? "#0F172A" : COLORS.emptyStroke;
                                const strokeW = selectedPosition === tdef.code ? 2.5 : 1.5;

                                // X position based on side and inner/outer
                                let x;
                                if (tdef.side === "C") {
                                    x = cx - TYRE_W / 2;
                                } else if (tdef.side === "L") {
                                    const offset = tdef.inner ? (TYRE_W + GAP) : (TYRE_W + GAP) * 2;
                                    x = cx - offset - 4;
                                } else {
                                    const offset = tdef.inner ? GAP : (TYRE_W + GAP * 2);
                                    x = cx + offset + 4;
                                }
                                const isClickable = !!onPositionClick;

                                return (
                                    <g
                                        key={tdef.code}
                                        onClick={() => isClickable && onPositionClick(tdef.code)}
                                        style={{ cursor: isClickable ? "pointer" : (tyre ? "pointer" : "default") }}
                                        data-testid={`tyre-pos-${tdef.code}`}
                                    >
                                        <rect x={x} y={y - TYRE_H / 2} width={TYRE_W} height={TYRE_H} rx={3}
                                            fill={fill} stroke={stroke} strokeWidth={strokeW} />
                                        <text x={x + TYRE_W / 2} y={y + 3} textAnchor="middle" fontSize="8" fontFamily="IBM Plex Mono" fontWeight="600"
                                            fill={tyre ? "#FFFFFF" : "#64748B"}>
                                            {tdef.code}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
                {layout.spare && (
                    <g
                        onClick={() => spareClickable && onPositionClick(layout.spare.code)}
                        style={{ cursor: spareClickable ? "pointer" : (spareTyre ? "pointer" : "default") }}
                        data-testid={`tyre-pos-${layout.spare.code}`}
                    >
                        {/* Spare bay — rendered as a small off-chassis compartment */}
                        <rect x={PAD_X + chassisW / 2 - 42} y={spareY - 6} width={84} height={38} rx={6}
                            fill="#F8FAFC" stroke="#CBD5E1" strokeDasharray="3 3" strokeWidth={1} />
                        <text x={PAD_X + chassisW / 2} y={spareY - 10} textAnchor="middle" fontSize="8"
                            fontFamily="IBM Plex Mono" fill="#94A3B8" letterSpacing="0.08em">SPARE</text>
                        <rect x={PAD_X + chassisW / 2 - TYRE_W / 2} y={spareY}
                            width={TYRE_W} height={TYRE_H - 6} rx={3}
                            fill={spareFill} stroke={spareStroke} strokeWidth={spareStrokeW} />
                        <text x={PAD_X + chassisW / 2} y={spareY + 16} textAnchor="middle" fontSize="8"
                            fontFamily="IBM Plex Mono" fontWeight="600"
                            fill={spareTyre ? "#FFFFFF" : "#64748B"}>
                            {layout.spare.code}
                        </text>
                    </g>
                )}
            </svg>

            <div className="flex-1 min-w-0">
                <div className="label-mono mb-2">{layout.label} · {layout.tyres} tyres{layout.spare ? " + 1 spare" : ""}</div>
                <div className="grid grid-cols-2 gap-2">
                    {layout.axles.flatMap((a) => a.tyres.map((tdef) => {
                        const tyre = byPos[tdef.code];
                        const isSelected = selectedPosition === tdef.code;
                        return (
                            <div
                                key={tdef.code}
                                onClick={() => onPositionClick && onPositionClick(tdef.code)}
                                className={`border rounded-md p-2.5 transition-colors ${isSelected ? "border-fleet-ink bg-slate-50" : "border-fleet-border bg-white"} ${onPositionClick ? "cursor-pointer hover:border-slate-400" : ""}`}
                                data-testid={`tyre-info-${tdef.code}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-mono text-xs font-semibold">{tdef.code}</div>
                                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: tyre ? colorForTyre(tyre) : "#CBD5E1" }} />
                                </div>
                                {tyre ? (
                                    <>
                                        <div className="font-mono text-xs mt-1 truncate">{tyre.serial_number}</div>
                                        <div className="text-[10px] text-fleet-inkSoft">{(tyre.live_km ?? tyre.total_km ?? 0).toLocaleString()}km{tyre.retread_count ? ` · ${tyre.retread_count} retread${tyre.retread_count > 1 ? "s" : ""}` : ""}</div>
                                    </>
                                ) : (
                                    <div className="text-[10px] text-fleet-inkMuted mt-1">Empty</div>
                                )}
                            </div>
                        );
                    }))}
                    {layout.spare && (() => {
                        const tdef = layout.spare;
                        const tyre = byPos[tdef.code];
                        const isSelected = selectedPosition === tdef.code;
                        return (
                            <div
                                key={tdef.code}
                                onClick={() => onPositionClick && onPositionClick(tdef.code)}
                                className={`col-span-2 border-2 border-dashed rounded-md p-2.5 transition-colors ${isSelected ? "border-fleet-ink bg-slate-50" : "border-fleet-border bg-slate-50/40"} ${onPositionClick ? "cursor-pointer hover:border-slate-400" : ""}`}
                                data-testid={`tyre-info-${tdef.code}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-xs font-semibold">{tdef.code}</span>
                                        <span className="label-mono text-[9px] px-1.5 py-0.5 bg-slate-200 rounded">SPARE</span>
                                    </div>
                                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: tyre ? colorForTyre(tyre) : "#CBD5E1" }} />
                                </div>
                                {tyre ? (
                                    <>
                                        <div className="font-mono text-xs mt-1 truncate">{tyre.serial_number}</div>
                                        <div className="text-[10px] text-fleet-inkSoft">{(tyre.live_km ?? tyre.total_km ?? 0).toLocaleString()}km{tyre.retread_count ? ` · ${tyre.retread_count} retread${tyre.retread_count > 1 ? "s" : ""}` : ""}</div>
                                    </>
                                ) : (
                                    <div className="text-[10px] text-fleet-inkMuted mt-1">Stepney slot empty — fit a tyre to use as a spare.</div>
                                )}
                            </div>
                        );
                    })()}
                </div>
                <div className="mt-3 flex gap-3 flex-wrap text-[10px] text-fleet-inkSoft">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" />≥5mm</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" />3–5mm</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-600" />&lt;3mm</span>
                </div>
            </div>
        </div>
    );
}
